import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const AuthPage = () => {
  const navigate = useNavigate();
  // Open in register mode if URL contains ?mode=register
  const [isLogin, setIsLogin] = useState(() => {
    if (typeof window === 'undefined') return true;
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') !== 'register';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, formatApiErrorDetail } = useAuth();
  const { theme } = useTheme();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success(t('auth_welcome_back'));
      } else {
        if (!name) { toast.error(t('auth_enter_name')); setLoading(false); return; }
        await register(name, email, password);
        toast.success(t('auth_account_created'));
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const apiDetail = err.response?.data?.detail;
      const diagnostic = [
        apiDetail ? formatApiErrorDetail(apiDetail) : null,
        err.message ? `message: ${err.message}` : null,
        err.code ? `code: ${err.code}` : null,
        err.response?.status ? `status: ${err.response.status}` : null,
        err.request?.responseURL ? `url: ${err.request.responseURL}` : null,
      ].filter(Boolean).join(' | ');

      toast.error(diagnostic || t('auth_generic_error'), { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: theme.bg }}>
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-5xl font-light tracking-tight mb-2" style={{ color: theme.accent }}>Teko</h1>
          <p className="text-base" style={{ color: theme.textSecondary }}>{t('auth_tagline')}</p>
        </div>

        <div className="p-8 rounded-lg" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="flex mb-6 rounded-md overflow-hidden" style={{ background: theme.elevated }}>
            <button data-testid="auth-login-tab" onClick={() => setIsLogin(true)}
              className="flex-1 py-2.5 text-sm font-medium transition-colors"
              style={{ background: isLogin ? theme.primary : 'transparent', color: isLogin ? '#fff' : theme.textMuted }}>
              {t('sign_in')}
            </button>
            <button data-testid="auth-register-tab" onClick={() => setIsLogin(false)}
              className="flex-1 py-2.5 text-sm font-medium transition-colors"
              style={{ background: !isLogin ? theme.primary : 'transparent', color: !isLogin ? '#fff' : theme.textMuted }}>
              {t('sign_up')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <Input data-testid="auth-name-input" placeholder={t('auth_full_name_placeholder')} value={name}
                onChange={(e) => setName(e.target.value)} autoFocus className="text-white"
                style={{ background: theme.elevated, borderColor: theme.border }} />
            )}
            <Input data-testid="auth-email-input" type="email" placeholder={t('email')} value={email}
              onChange={(e) => setEmail(e.target.value)} autoFocus={isLogin} className="text-white"
              style={{ background: theme.elevated, borderColor: theme.border }} />
            <Input data-testid="auth-password-input" type="password" placeholder={t('password')} value={password}
              onChange={(e) => setPassword(e.target.value)} className="text-white"
              style={{ background: theme.elevated, borderColor: theme.border }} />
            <Button data-testid="auth-submit-button" type="submit" disabled={loading}
              className="w-full text-white" style={{ background: theme.primary }}>
              {loading ? t('loading') : isLogin ? t('sign_in') : t('auth_create_account')}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: `1px solid ${theme.border}` }} /></div>
            <div className="relative flex justify-center text-sm"><span className="px-3" style={{ background: theme.card, color: theme.textMuted }}>{t('or_continue_with')}</span></div>
          </div>

          <button data-testid="google-login-button" onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-6 py-2.5 rounded-md font-medium transition-all"
            style={{ background: theme.elevated, border: `1px solid ${theme.border}`, color: theme.textPrimary }}>
            <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t('continue_with_google')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
